// EmsModeTile — prominente Safe/Active toggle voor HomePage.
//
// Voor incident-use: wanneer er iets mis gaat wil je in één klik naar SAFE
// (geen Modbus-writes). Active → Safe is laag-risico (alles stopt). Safe →
// Active is hoog-risico (EMS gaat schrijven). Dat tweede vraagt expliciete
// bevestiging.
//
// Onder de motorkap loopt elke richting sinds punt 61 over een eigen route;
// de generieke override-route weigert `ems.mode` met HTTP 400.
//
//   naar SAFE     POST /api/admin/ems-safe-now   geen body, herstart de
//                                                services zelf (~3s totaal)
//   naar ACTIVE   POST /api/admin/ems-activate   body {confirm_site: "<naam>",
//                                                trigger_restart: true}
//
// De operator typt de sitenaam zelf in. Dat is het punt van `confirm_site`:
// het onderscheidt "ik activeer deze Pi" van "ik voer een handeling uit die
// ergens anders is bedacht". Een knop die het veld voor je invult haalt die
// eigenschap er aan de browserkant weer uit, dus dat doen we niet. De naam
// wordt serverzijdig hoofdletterongevoelig vergeleken; klopt hij niet, dan
// weigert de server met een 400, wordt er niets geschreven en niets herstart,
// en tonen we die melding in de bevestiging.
//
// De herstart vragen we mee, zodat het klikken van de knop en het effect op de
// omvormer één moment blijven. Blijft die herstart onverhoopt uit, dan draait
// de engine nog in shadow-only en zegt de tegel dat met zoveel woorden.
//
// De /api/health pollt elke 30s in App.tsx en /api/snapshot elke 5s — de tile
// toont de echte nieuwe state binnen één poll-cyclus na die herstart.

import { useEffect, useRef, useState } from 'react';
import { submitEmsActivate, submitEmsSafeNow } from '../../api/adminOverride';

type EmsMode = 'safe' | 'active';

interface EmsModeTileProps {
  /** Huidige ems_mode uit /api/snapshot — pollt elke 5s. */
  currentMode: EmsMode | string | undefined;
  /** Hint-tekst onder de pillenrij. Default = strategy of "—". */
  hint?: string;
  /**
   * installation.name van deze site, uit /api/config/installation. Wordt
   * uitsluitend gebruikt om in de bevestiging te benoemen wélke naam gevraagd
   * wordt. Het invoerveld wordt er bewust niet mee voorgevuld.
   */
  siteName?: string;
  /**
   * Heeft de ingelogde gebruiker de admin-rol (uit /api/auth/status)?
   *
   * Alleen de weg NAAR ACTIVE hangt eraan: `/api/admin/ems-activate` staat op
   * ADMIN. De weg naar SAFE loopt over `/api/admin/ems-safe-now`, en die staat
   * bewust op SELF_SERVICE — stoppen met schrijven is de veilige richting en
   * moet voor iedereen bereikbaar blijven. Dezelfde asymmetrie als bij de
   * force-actie: starten is admin, afbreken niet.
   *
   * Onbekend (undefined) telt als geen admin: dat is de kant waar een
   * vergissing alleen een knop kost en geen ongevraagde writes op de omvormer.
   */
  isAdmin?: boolean;
}

export function EmsModeTile({ currentMode, hint, siteName, isAdmin }: EmsModeTileProps) {
  const mayActivate = isAdmin === true;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmTo, setConfirmTo] = useState<EmsMode | null>(null);
  // Vrije invoer van de operator voor de activering. Nooit voorgevuld.
  const [confirmSite, setConfirmSite] = useState('');
  // Alleen gevuld als de server de activering wel schreef maar geen herstart
  // deed: de engine draait dan nog in shadow-only en dat mag de operator niet
  // zelf hoeven af te leiden uit een tegel die SAFE blijft tonen.
  const [activateNote, setActivateNote] = useState<string | null>(null);
  // Optimistic state — toont meteen het doel tijdens busy + post-write window
  const [optimistic, setOptimistic] = useState<EmsMode | null>(null);
  const optimisticTimer = useRef<number | undefined>(undefined);

  // Bij elke server-update: drop optimistic als snap die echt gepakt heeft.
  useEffect(() => {
    if (optimistic && currentMode === optimistic) {
      setOptimistic(null);
      if (optimisticTimer.current !== undefined) {
        window.clearTimeout(optimisticTimer.current);
        optimisticTimer.current = undefined;
      }
    }
  }, [currentMode, optimistic]);

  // Zodra de site echt ACTIVE draait is de herinnering aan de herstart klaar.
  useEffect(() => {
    if (currentMode === 'active') setActivateNote(null);
  }, [currentMode]);

  const displayMode: EmsMode = (optimistic ?? (currentMode === 'safe' ? 'safe' : currentMode === 'active' ? 'active' : 'safe')) as EmsMode;
  const isActive = displayMode === 'active';

  async function performFlip(target: EmsMode) {
    setBusy(true);
    setError(null);
    setOptimistic(target);
    // Veiligheidsnet: als de server na 20s nog niet de nieuwe waarde toont,
    // wist optimistic en laat de echte server-state weer winnen (dan zie je
    // dat de change niet door is gekomen).
    if (optimisticTimer.current !== undefined) {
      window.clearTimeout(optimisticTimer.current);
    }
    optimisticTimer.current = window.setTimeout(() => {
      setOptimistic(null);
    }, 20_000);
    try {
      if (target === 'safe') {
        // Paniek-pad: direct SC disengage + 43110 baseline + safe-mode override
        // (alles atomair via dedicated endpoint). Bram-PRIO 2026-05-19.
        await submitEmsSafeNow();
        setActivateNote(null);
      } else {
        const res = await submitEmsActivate(confirmSite.trim());
        setActivateNote(
          res.restart_triggered
            ? null
            : `Activering geschreven voor ${res.site}, maar de engine is niet `
              + 'herstart. Tot een herstart van de loop blijft de writer in '
              + 'shadow-only.',
        );
      }
      setConfirmTo(null);
      setConfirmSite('');
    } catch (e) {
      setOptimistic(null);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function handleClick(target: EmsMode) {
    if (target === displayMode) return;
    // Active → Safe = laagrisico maar bevestig wel (één klik te ver = ongewenst stop).
    // Safe → Active = HOOGRISICO, expliciete confirm plus getypte sitenaam.
    setError(null);
    setConfirmSite('');
    setConfirmTo(target);
  }

  function closeConfirm() {
    setConfirmTo(null);
    setConfirmSite('');
  }

  // Naar SAFE mag altijd door; naar ACTIVE pas als er iets is ingevuld. De
  // inhoud toetsen we niet vooraf — dat doet de server, en die is de enige
  // partij die weet hoe deze Pi heet.
  const canConfirm = !busy && (confirmTo !== 'active' || confirmSite.trim().length > 0);

  return (
    <div className={`ems-mode-tile${isActive ? ' is-active' : ' is-safe'}`}>
      <div className="ems-mode-tile__header">
        <div className="ems-mode-tile__label">
          <span className="ems-mode-tile__dot" aria-hidden="true" />
          EMS-modus
        </div>
        <span className="ems-mode-tile__status">
          {isActive ? 'schrijft' : 'shadow'}
        </span>
      </div>
      <div className="ems-mode-tile__switch" role="group" aria-label="EMS-modus selectie">
        <button
          type="button"
          aria-pressed={displayMode === 'safe'}
          className={`ems-mode-tile__btn ems-mode-tile__btn--safe${displayMode === 'safe' ? ' is-active' : ''}`}
          disabled={busy}
          onClick={() => handleClick('safe')}
        >
          <span className="ems-mode-tile__btn-label">SAFE</span>
          <span className="ems-mode-tile__btn-sub">stop writes</span>
        </button>
        <button
          type="button"
          aria-pressed={displayMode === 'active'}
          className={`ems-mode-tile__btn ems-mode-tile__btn--active${displayMode === 'active' ? ' is-active' : ''}`}
          disabled={busy || !mayActivate}
          onClick={() => handleClick('active')}
        >
          <span className="ems-mode-tile__btn-label">ACTIVE</span>
          <span className="ems-mode-tile__btn-sub">EMS regelt</span>
        </button>
      </div>
      {!mayActivate && (
        // Uitgeschakeld en niet verborgen: de tegel blijft de modus tonen, en
        // wie hem niet kan omzetten leest waarom in plaats van dat hij het bij
        // het indrukken als foutmelding te horen krijgt.
        <div className="ems-mode-tile__hint" data-testid="ems-mode-admin-note">
          Het systeem weer laten regelen vereist de beheerdersrol. Stoppen met
          regelen kan wel.
        </div>
      )}
      {hint && <div className="ems-mode-tile__hint">{hint}</div>}
      {activateNote && <div className="ems-mode-tile__hint">{activateNote}</div>}
      {/* Staat de bevestiging open, dan hoort de fout dáár — achter de
          backdrop is hij onleesbaar. */}
      {error && !confirmTo && <div className="ems-mode-tile__error">{error}</div>}

      {confirmTo && (
        <div className="ems-mode-modal-backdrop" role="dialog" aria-modal="true">
          <div className="ems-mode-modal">
            <h3 className="ems-mode-modal__title">
              {confirmTo === 'safe' ? 'Naar SAFE schakelen?' : 'Naar ACTIVE schakelen?'}
            </h3>
            {confirmTo === 'safe' ? (
              <p className="ems-mode-modal__body">
                EMS stopt onmiddellijk met schrijven naar de inverter. SC TOU wordt direct
                ge­disengageerd (44100=0) en de inverter krijgt Self-Use + Batt OVC baseline
                (43110=0x81) — geen feed-in priority of off-grid mode. Service herstart
                ~1s; eind-tot-eind ~3s.
              </p>
            ) : (
              <>
                <div className="ems-mode-modal__warning">
                  <strong>Let op:</strong> EMS gaat actief schrijven naar de inverter
                  (43132/43133, SC TOU). De wijziging wordt in de config vastgelegd en
                  de services worden herstart (~3s downtime). Doe dit alleen als je
                  weet wat je doet — eerste activatie hoort met Diederik on-site te
                  gebeuren.
                </div>
                <label className="ems-mode-modal__confirm-site">
                  <span>
                    Typ de naam van deze site
                    {siteName ? ` (installation.name is "${siteName}")` : ' (installation.name)'}
                    {' '}om te bevestigen dat je de juiste Pi activeert.
                  </span>
                  <input
                    type="text"
                    autoComplete="off"
                    value={confirmSite}
                    disabled={busy}
                    placeholder="sitenaam"
                    onChange={(e) => setConfirmSite(e.target.value)}
                  />
                </label>
              </>
            )}
            {error && <div className="ems-mode-modal__error">{error}</div>}
            <div className="ems-mode-modal__actions">
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={closeConfirm}
              >
                Annuleren
              </button>
              <button
                type="button"
                className={confirmTo === 'safe' ? 'btn-primary' : 'btn-danger'}
                disabled={!canConfirm}
                onClick={() => performFlip(confirmTo)}
              >
                {busy ? 'Bezig…' : confirmTo === 'safe' ? 'Schakel SAFE' : 'Schakel ACTIVE'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
